import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { apiFetch } from '@/lib/api';

interface CanvasTemplate {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  sort_order: number;
  use_case_ids: string[];
  node_count: number;
  creator_name: string | null;
  created_at: string;
  updated_at: string;
}

interface UseCase {
  id: string;
  label: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
}

interface CanvasSearchResult {
  id: string;
  name: string;
  description: string | null;
  organization_id: string;
  org_name: string;
  updated_at: string;
}

export function CanvasTemplates() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState<CanvasTemplate | null>(null);
  const [search, setSearch] = React.useState('');
  const [selectedCanvas, setSelectedCanvas] = React.useState<CanvasSearchResult | null>(null);
  const [templateName, setTemplateName] = React.useState('');
  const [templateDescription, setTemplateDescription] = React.useState('');
  const [selectedUseCases, setSelectedUseCases] = React.useState<string[]>([]);

  // Fetch templates
  const { data: templates, isLoading } = useQuery<CanvasTemplate[]>({
    queryKey: ['admin', 'canvas-templates'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/canvas-templates');
      if (!res.ok) throw new Error('Failed to fetch templates');
      const json = await res.json();
      return json.success ? json.data : [];
    },
  });

  // Fetch use cases for selection
  const { data: useCases } = useQuery<UseCase[]>({
    queryKey: ['admin', 'use-cases'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/use-cases');
      if (!res.ok) throw new Error('Failed to fetch use cases');
      const json = await res.json();
      return json.success ? json.data : [];
    },
  });

  // Search canvases
  const { data: searchResults, isFetching: isSearching } = useQuery<CanvasSearchResult[]>({
    queryKey: ['admin', 'canvases-search', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const res = await apiFetch(`/api/admin/canvases?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to search canvases');
      const json = await res.json();
      return json.success ? json.data : [];
    },
    enabled: showAddModal,
  });

  // Create template mutation
  const createMutation = useMutation({
    mutationFn: async (data: { canvas_id: string; org_id: string; name: string; description: string | null }) => {
      const res = await apiFetch('/api/admin/canvas-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Failed to create template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'canvas-templates'] });
      handleCloseModal();
    },
  });

  // Toggle enabled mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await apiFetch(`/api/admin/canvas-templates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error('Failed to update template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'canvas-templates'] });
    },
  });

  // Update mutation (for name/description/use_case_ids)
  const updateMutation = useMutation({
    mutationFn: async ({ id, name, description, use_case_ids }: { id: string; name: string; description: string | null; use_case_ids: string[] }) => {
      const res = await apiFetch(`/api/admin/canvas-templates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, use_case_ids }),
      });
      if (!res.ok) throw new Error('Failed to update template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'canvas-templates'] });
      handleCloseEditModal();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/admin/canvas-templates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'canvas-templates'] });
    },
  });

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSearch('');
    setSelectedCanvas(null);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedUseCases([]);
  };

  const handleOpenEditModal = (template: CanvasTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setSelectedUseCases(template.use_case_ids || []);
  };

  const handleCloseEditModal = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedUseCases([]);
  };

  const toggleUseCase = (id: string) => {
    setSelectedUseCases(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleUpdateTemplate = () => {
    if (!editingTemplate || !templateName.trim()) return;
    updateMutation.mutate({
      id: editingTemplate.id,
      name: templateName.trim(),
      description: templateDescription.trim() || null,
      use_case_ids: selectedUseCases,
    });
  };

  const handleSelectCanvas = (canvas: CanvasSearchResult) => {
    setSelectedCanvas(canvas);
    setTemplateName(canvas.name);
    setTemplateDescription(canvas.description || '');
  };

  const handleCreateTemplate = () => {
    if (!selectedCanvas || !templateName.trim()) return;
    createMutation.mutate({
      canvas_id: selectedCanvas.id,
      org_id: selectedCanvas.organization_id,
      name: templateName.trim(),
      description: templateDescription.trim() || null,
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <PageContainer className="max-w-6xl mx-auto">
      <PageHeader
        title="Canvas Templates"
        description="Manage onboarding templates that are automatically copied to new organizations"
      >
        <Button onClick={() => setShowAddModal(true)}>
          Add Template
        </Button>
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !templates?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-gray-500 mb-4">No templates yet</div>
            <Button onClick={() => setShowAddModal(true)}>Add your first template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <Card key={template.id}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      {template.enabled ? (
                        <Badge variant="success">Enabled</Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                      <span>{template.node_count} nodes</span>
                      <span>Created {formatDate(template.created_at)}</span>
                      {template.creator_name && <span>by {template.creator_name}</span>}
                    </div>
                    {template.use_case_ids && template.use_case_ids.length > 0 ? (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="text-xs text-gray-400">Use cases:</span>
                        {template.use_case_ids.map(ucId => {
                          const uc = useCases?.find(u => u.id === ucId);
                          return (
                            <Badge key={ucId} variant="outline">
                              {uc?.label || ucId}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <Badge variant="secondary">General (all users)</Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-500">Enabled</span>
                      <Switch
                        checked={template.enabled}
                        onChange={(e) => toggleMutation.mutate({ id: template.id, enabled: e.target.checked })}
                      />
                    </label>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEditModal(template)}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Delete this template? This won\'t affect canvases already created from it.')) {
                          deleteMutation.mutate(template.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Template Modal */}
      <Modal isOpen={showAddModal} onClose={handleCloseModal} title="Add Canvas Template">
        <div className="space-y-4">
          {!selectedCanvas ? (
            <>
              <Input
                label="Search canvases"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500">Searching...</div>
                ) : !searchResults?.length ? (
                  <div className="p-4 text-center text-gray-500">
                    {search ? 'No canvases found' : 'Type to search for canvases'}
                  </div>
                ) : (
                  searchResults.map((canvas) => (
                    <button
                      key={canvas.id}
                      onClick={() => handleSelectCanvas(canvas)}
                      className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{canvas.name}</div>
                      <div className="text-sm text-gray-500">{canvas.org_name}</div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Selected canvas:</div>
                <div className="font-medium">{selectedCanvas.name}</div>
                <div className="text-sm text-gray-500">{selectedCanvas.org_name}</div>
                <button
                  onClick={() => setSelectedCanvas(null)}
                  className="text-sm text-primary-600 hover:underline mt-1"
                >
                  Change selection
                </button>
              </div>

              <Input
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
              />

              <Textarea
                label="Description (optional)"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Describe what this template is for"
                rows={3}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTemplate}
                  disabled={!templateName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Template'}
                </Button>
              </div>

              {createMutation.error && (
                <div className="text-sm text-red-600">
                  {createMutation.error.message}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Edit Template Modal */}
      <Modal isOpen={!!editingTemplate} onClose={handleCloseEditModal} title="Edit Template">
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
          />

          <Textarea
            label="Description (optional)"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Describe what this template is for"
            rows={3}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Use Cases (leave empty for all users)
            </label>
            <div className="flex flex-wrap gap-2">
              {useCases?.map(uc => (
                <button
                  key={uc.id}
                  type="button"
                  onClick={() => toggleUseCase(uc.id)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    selectedUseCases.includes(uc.id)
                      ? 'bg-primary-100 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {selectedUseCases.includes(uc.id) && (
                    <span className="mr-1">&#10003;</span>
                  )}
                  {uc.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedUseCases.length === 0
                ? 'This template will be copied to all new organizations'
                : `This template will only be copied to organizations that select: ${selectedUseCases.map(id => useCases?.find(u => u.id === id)?.label).join(', ')}`}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTemplate}
              disabled={!templateName.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {updateMutation.error && (
            <div className="text-sm text-red-600">
              {updateMutation.error.message}
            </div>
          )}
        </div>
      </Modal>
    </PageContainer>
  );
}
